/* eslint-disable indent, react/jsx-indent */
import './App.css';
import * as React from 'react';
import { BootAppProvider, BootAppProviderProps } from '@search/vtbeco-frontend-core/domain/boot/AppProvider';
import { useOfferQuery } from '@search/vtbeco-frontend-core/domain/offer/search/page/OfferQuery';
import { OfferRoute, SearchRoute, SearchMapRoute, useRouter, UnsubscribeRoute } from '@search/offer-search-routes/src';
import classname from '@search/classname/src';
import { Source } from '@search/vtbeco-frontend-core/view/common/components/MinimalBanksMortgage/MortgageAppSourceContext';

import { ApplicationIdEnum } from '@search/graphql-typings';
import { errorCatcherWrap } from '@search/error/src/catcher';
import ClHeader from '@search/classified-common/src/ClShellProvider/ClHeader';
import ClFooter from '@search/classified-common/src/ClShellProvider/ClFooter';
import { ClShellProvider } from '@search/classified-common/src/ClShellProvider/ClShellProvider';
// preferences-business-api, см. NB-6873, поэтому юзаем заглушку
import { NoopAgentFavoritesProvider as AgentFavoritesProvider } from '@search/classified-common/src/Agent/AgentFavoritesProvider/NoopAgentFavoritesProvider';
import { ApplicationProvider } from '@search/hooks/src/providers/ApplicationProvider/ApplicationProvider';
import { UnsubscribeFromEmails } from '@search/vtbeco-frontend-core/view/common/components/UnsubscribeFromEmails/UnsubscribeFromEmails';
import { initCookieModalHack } from '@search/vtbeco-frontend-core/init-cookie-modal-hack';

import { useClGeoRootRegionUrlSwitch } from '@search/geo/src/clGeoRootRegion/useClGeoRootRegionUrlSwitch';
import { OffersSearch } from './components/OffersSearch';
import { OfferSearchMap } from './components/OfferSearchMap';
import { OfferPage } from './components/pages';
import { OfferPageNotFoundDesk } from './components/pages/OfferPage/OfferPageNotFoundDesk';

import OfferCardSprite from '-!svg-react-loader!@search/classified-common/src/sprites/offerCardSprite.svg';

const cn = classname.bind(null, 'App');

const AppContexted = errorCatcherWrap(function AppContexted() {
    const router = useRouter();
    const offerLoader = useOfferQuery();
    let promoRegion: number | undefined;
    let page: React.ReactNode = null;
    let hasBackground = true;

    const { RouteClass } = router.resolve([ SearchRoute, OfferRoute, SearchMapRoute, UnsubscribeRoute ]);

    useClGeoRootRegionUrlSwitch();

    if (RouteClass === SearchRoute) {
        const searchRoute = router.route(SearchRoute);
        const searchParams = searchRoute.params();

        promoRegion = searchParams.region;
        page = <OffersSearch />;
    } else if (RouteClass === OfferRoute) {
        const { offerId } = router.route(OfferRoute).params();
        const { data, errors } = offerLoader({ offerId });
        const offer = data?.offer.__typename === 'ProfOfferSnippet' ? data.offer : undefined;
        const location = offer?.location;

        // eslint-disable-next-line no-nested-ternary
        promoRegion = location?.narrowRegion?.id;

        const isNotFound = errors?.[0].code === 'NOT_FOUND' ||
            data?.offer.__typename === 'ProfOfferError' ||
            (
                data?.offer.__typename === 'ProfOfferSnippet' &&
                data.offer.status?.visibility !== 'EVERYONE'
            );

        page = isNotFound ? <OfferPageNotFoundDesk /> : (<>
            <div className='CardSprite' style={{ display: 'none' }}>
                <OfferCardSprite />
            </div>
            <OfferPage data={data} />
        </>);
        hasBackground = isNotFound;
    } else if (RouteClass === SearchMapRoute) {
        const searchMapRoute = router.route(SearchMapRoute);
        const searchMapParams = searchMapRoute.params();

        promoRegion = searchMapParams.region;
        page = <OfferSearchMap />;
    } else if (RouteClass === UnsubscribeRoute) {
        page = <UnsubscribeFromEmails />;
    }

    return (
        <AgentFavoritesProvider>
            <div className={cn(null, { colored: hasBackground })}>
                <ClShellProvider promoRegion={promoRegion}>
                    <ClHeader
                        regionId={promoRegion}
                        isFullWidth={! (RouteClass === SearchRoute || RouteClass === OfferRoute)}
                        isBordered={RouteClass === SearchRoute}
                    />
                    <main className={RouteClass === SearchMapRoute ? '' : cn('main')}>
                        <React.Suspense fallback={null}>
                            {page}
                        </React.Suspense>
                    </main>
                    {RouteClass === SearchMapRoute ? null : (
                            <div className={cn('footer', { secondaryBackground: RouteClass === OfferRoute })}>
                                <ClFooter regionId={promoRegion} />
                            </div>
                    )}
                </ClShellProvider>
            </div>
        </AgentFavoritesProvider>
    );
});

export function App({
    googleOptimize,
    router,
    rootRegions,
    auth,
    isMobile,
    catcher,
    gql,
    cookies,
    yMapsApiKey,
    staticPath,
    developerPromoConfigs
}: Omit<BootAppProviderProps, 'mortgageSource'>) {
    initCookieModalHack();

    return (
        <BootAppProvider
            isMobile={isMobile}
            googleOptimize={googleOptimize}
            catcher={catcher}
            gql={gql}
            router={router}
            rootRegions={rootRegions}
            auth={auth}
            cookies={cookies}
            yMapsApiKey={yMapsApiKey}
            staticPath={staticPath}
            mortgageSource={Source.COMFORT_SEARCH_M2}
            developerPromoConfigs={developerPromoConfigs}
        >
            <ApplicationProvider applicationId={ApplicationIdEnum.ClassifiedDesktop}>
                <AppContexted />
            </ApplicationProvider>
        </BootAppProvider>
    );
}

